<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/vue/24/outline'

const SNOOZE_DURATION = 30 * 60 * 1000
const APPLY_TIMEOUT = 15 * 1000
const RELOAD_GUARD_KEY = 'jarvis:pwa-update-attempt'
const currentVersion = typeof __APP_VERSION_DISPLAY__ !== 'undefined'
  ? __APP_VERSION_DISPLAY__
  : String(__APP_VERSION__).replaceAll('-', '.')

const snoozed = ref(false)
const reloading = ref(false)
const blockedAttempt = ref(false)
const errorMessage = ref('')
const latestVersion = ref('')
const releaseSummary = ref('Pembaruan dashboard sudah selesai diunduh dan siap dipakai.')
const highlights = ref([])
let applyTimer = null
let snoozeTimer = null

const targetVersion = computed(() => latestVersion.value || 'versi terbaru')

// Service worker baru bisa muncul tanpa perubahan versi (build ulang, file
// statis berubah). Popup hanya layak tampil bila server benar-benar punya versi
// yang lebih baru; selain itu worker baru cukup aktif sendiri saat semua tab
// ditutup, tanpa mengganggu pengguna.
const versionParts = (value) => String(value || '').split('.').map((part) => Number.parseInt(part, 10) || 0)
const isNewerVersion = (candidate, installed) => {
  const a = versionParts(candidate)
  const b = versionParts(installed)
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    if ((a[i] || 0) !== (b[i] || 0)) return (a[i] || 0) > (b[i] || 0)
  }
  return false
}
const hasNewerVersion = computed(() => isNewerVersion(latestVersion.value, currentVersion))

const loadReleaseInfo = async () => {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
    if (!response.ok) return
    const info = await response.json()
    latestVersion.value = info.version || ''
    releaseSummary.value = info.summary || releaseSummary.value
    highlights.value = Array.isArray(info.highlights) ? info.highlights.slice(0, 3) : []
    // Guard menyimpan versi TUJUAN. Kalau halaman sudah memuat ulang tetapi
    // masih versi lama untuk tujuan yang sama, jangan ulangi reload.
    blockedAttempt.value = sessionStorage.getItem(RELOAD_GUARD_KEY) === latestVersion.value
  } catch (error) {
    console.warn('Informasi versi terbaru tidak dapat dimuat', error)
  }
}

const { needRefresh, updateServiceWorker } = useRegisterSW({
  onNeedRefresh() {
    snoozed.value = false
    loadReleaseInfo()
  },
  onRegisterError(error) {
    console.error('Service worker gagal didaftarkan', error)
  },
})

// Percobaan sebelumnya berhasil bila versi yang kini terpasang sudah sama
// atau lebih baru dari versi tujuannya; guard tidak diperlukan lagi.
const previousAttempt = sessionStorage.getItem(RELOAD_GUARD_KEY) || ''
if (previousAttempt && !isNewerVersion(previousAttempt, currentVersion)) {
  sessionStorage.removeItem(RELOAD_GUARD_KEY)
}

const applyUpdate = async () => {
  if (reloading.value || blockedAttempt.value) return
  reloading.value = true
  errorMessage.value = ''
  sessionStorage.setItem(RELOAD_GUARD_KEY, latestVersion.value)
  try {
    // vite-plugin-pwa melakukan reload saat worker baru mulai mengontrol.
    // Guard session mencegah build lama mencoba reload yang sama berulang.
    await updateServiceWorker(false)
    applyTimer = setTimeout(() => {
      reloading.value = false
      blockedAttempt.value = true
      errorMessage.value = 'Pembaruan belum berhasil mengambil alih. Tutup lalu buka kembali aplikasi agar pekerjaan tetap aman.'
    }, APPLY_TIMEOUT)
  } catch (error) {
    reloading.value = false
    sessionStorage.removeItem(RELOAD_GUARD_KEY)
    errorMessage.value = 'Pembaruan gagal diterapkan. Periksa koneksi lalu coba lagi.'
    console.error('Service worker gagal diperbarui', error)
  }
}

const snooze = () => {
  snoozed.value = true
  clearTimeout(snoozeTimer)
  snoozeTimer = setTimeout(() => { snoozed.value = false }, SNOOZE_DURATION)
}

onBeforeUnmount(() => {
  clearTimeout(applyTimer)
  clearTimeout(snoozeTimer)
})
</script>

<template>
  <Transition name="update-banner">
    <aside
      v-if="needRefresh && hasNewerVersion && !snoozed"
      class="fixed bottom-4 left-1/2 z-[100] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-slate-900/10 sm:left-auto sm:right-4 sm:translate-x-0"
      role="status"
      aria-live="polite"
      aria-label="Pembaruan aplikasi tersedia"
    >
      <div class="flex items-start gap-3 p-4">
        <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#B70000]/10 text-[#B70000]">
          <ArrowPathIcon class="h-5 w-5" :class="reloading ? 'animate-spin' : ''" />
        </span>
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-2">
            <p class="text-sm font-bold text-slate-950">Versi baru siap digunakan</p>
            <span v-if="latestVersion" class="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-800">v{{ latestVersion }}</span>
          </div>
          <p class="mt-1 text-xs leading-5 text-slate-600">{{ releaseSummary }}</p>
          <ul v-if="highlights.length" class="mt-2 space-y-1 text-xs text-slate-700">
            <li v-for="item in highlights" :key="item" class="flex gap-1.5">
              <CheckCircleIcon class="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
              <span>{{ item }}</span>
            </li>
          </ul>
          <p v-if="errorMessage" class="mt-2 flex gap-1.5 rounded-lg bg-amber-50 px-2.5 py-2 text-xs font-semibold leading-5 text-amber-900" role="alert">
            <ExclamationTriangleIcon class="mt-0.5 h-4 w-4 shrink-0" />
            <span>{{ errorMessage }}</span>
          </p>
          <div class="mt-3 flex flex-wrap gap-2">
            <button type="button" class="min-h-9 rounded-lg bg-[#B70000] px-3.5 text-xs font-bold text-white hover:bg-[#930000] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-700/30 disabled:cursor-not-allowed disabled:bg-slate-300" :disabled="reloading || blockedAttempt" @click="applyUpdate">
              {{ reloading ? 'Menerapkan…' : blockedAttempt ? 'Sudah dicoba' : 'Muat ulang & perbarui' }}
            </button>
            <button type="button" class="min-h-9 rounded-lg px-3 text-xs font-bold text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400" @click="snooze">Nanti saja</button>
          </div>
        </div>
        <button type="button" class="shrink-0 rounded-lg p-1 text-slate-500 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400" aria-label="Tutup pemberitahuan" @click="snooze">
          <XMarkIcon class="h-4 w-4" />
        </button>
      </div>
      <p class="bg-slate-50 px-4 py-2 text-[11px] text-slate-600">Terpasang v{{ currentVersion }} <span aria-hidden="true">→</span> {{ latestVersion ? `tersedia v${targetVersion}` : 'versi terbaru tersedia' }}</p>
    </aside>
  </Transition>
</template>

<style scoped>
.update-banner-enter-active,
.update-banner-leave-active { transition: opacity 200ms ease, transform 200ms ease; }
.update-banner-enter-from,
.update-banner-leave-to { opacity: 0; transform: translateY(12px); }
@media (prefers-reduced-motion: reduce) {
  .update-banner-enter-active,
  .update-banner-leave-active { transition: none; }
  .update-banner-enter-from,
  .update-banner-leave-to { transform: none; }
}
</style>
